import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * AuthCallback — one-shot handler for the Google OAuth return trip.
 *
 * Emergent-managed Google Auth redirects the browser back here with
 *   #session_id=<opaque token>
 * We POST that session_id to our backend, which exchanges it for our own JWT
 * and returns the user. Then we route the user to /billing (no plan) or /jobs.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
const AuthCallback = () => {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const done = useRef(false); // guards StrictMode double-invoke

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      toast.error("Google sign-in failed — no session id");
      nav("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        const r = await api.post("/auth/google/session", { session_id: sessionId });
        localStorage.setItem("fp_token", r.data.access_token);
        localStorage.setItem("fp_user", JSON.stringify(r.data.user));
        await refreshUser();
        const u = r.data.user || {};
        const active = u.subscription_status === "active" || u.subscription_tier === "lifetime";
        toast.success("Signed in with Google");
        nav(active ? "/jobs" : "/billing", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Google sign-in failed");
        nav("/login", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="border-2 border-foreground bg-background p-6 shadow-block-sm">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Signing you in…</div>
        <div className="font-heading font-black uppercase text-2xl mt-1">Google sign-in</div>
      </div>
    </div>
  );
};

export default AuthCallback;
